<template>
    <section class="kine-reeducation card mb-3">
        <div class="card-body">
            <p>{{ context.reeducation.motif || 'Motif non renseigné' }} · prescrite par {{ context.reeducation.prescripteur || 'prescripteur non renseigné' }} le {{ prescriptionDate }}</p>
            <h3>Séance {{ context.numero }} / {{ total }}</h3>
            <details class="mt-3">
                <summary>Bilan initial — lecture seule</summary>
                <section v-for="(fields, slug) in (context.bilan || {}).rubriques" :key="slug" class="mt-3">
                    <h4>{{ label(slug) }}</h4>
                    <dl v-for="(field, index) in fields" :key="index">
                        <dt>{{ field.field_name }}</dt><dd>{{ value(field.value) }}</dd>
                        <dd v-for="(row, rowIndex) in (field.rows || [])" :key="rowIndex">{{ grid(row, slug) }}</dd>
                    </dl>
                </section>
            </details>
            <button type="button" class="btn btn-primary-o btn-radius mt-3" :disabled="reevaluation" @click="$emit('reevaluer')">{{ reevaluation ? 'Réévaluation ajoutée' : 'Réévaluer' }}</button>
            <h4 class="mt-3">Séances précédentes</h4>
            <table v-if="previous.length" class="kine-table">
                <thead><tr><th>Séance</th><th>Douleur avant → après</th><th>Tolérance</th></tr></thead>
                <tbody><tr v-for="session in previous" :key="session.consultation_id">
                    <td data-label="Séance"><strong>{{ session.numero }}</strong><small>{{ date(session.date) }}</small></td>
                    <td data-label="Douleur">{{ recorded(session.douleur_avant) }} → {{ recorded(session.douleur_apres) }}</td>
                    <td data-label="Tolérance">{{ session.tolerance || 'Non renseignée' }}</td>
                </tr></tbody>
            </table>
            <p v-else>Aucune séance précédente.</p>
            <div v-if="Number(context.numero) >= total && context.reeducation.statut === 'en_cours'" class="mt-3">
                <p>Dernière séance prescrite : choisissez la suite de la rééducation.</p>
                <button type="button" class="btn btn-primary-o btn-radius mr-2" @click="decision = 'prolonger'">Prolonger</button>
                <button type="button" class="btn btn-primary-o btn-radius" @click="decision = 'terminer'">Terminer la rééducation</button>
            </div>
            <b-modal :visible="!!decision" title="Suite de la rééducation" size="lg" hide-footer :no-close-on-backdrop="saving" :no-close-on-esc="saving" @hide="beforeHide">
                <form @submit.prevent="save">
                    <p v-if="error" class="alert alert-danger" role="alert">{{ error }}</p>
                    <fieldset :disabled="saving">
                        <div v-if="decision === 'prolonger'" class="form-group"><label for="kine-extension">Séances supplémentaires</label><input id="kine-extension" v-model.number="seances" type="number" min="1" max="50" required class="form-control"></div>
                        <div v-else class="form-group"><label for="kine-end-state">Décision</label><select id="kine-end-state" v-model="statut" required class="form-control"><option value="terminee">Terminer</option><option value="interrompue">Interrompre</option></select></div>
                        <div class="form-group"><label for="kine-decision-reason">Motif</label><textarea id="kine-decision-reason" v-model="motif" minlength="5" maxlength="500" required class="form-control" rows="3"></textarea></div>
                    </fieldset>
                    <div class="ui-modal-actions"><button type="button" class="btn btn-secondary-light-o btn-radius" :disabled="saving" @click="decision = ''">Annuler</button><button type="submit" class="btn btn-primary btn-radius" :disabled="saving">{{ saving ? 'Enregistrement…' : 'Enregistrer' }}</button></div>
                </form>
            </b-modal>
        </div>
    </section>
</template>
<script>
import { BModal } from 'bootstrap-vue'
import api from '@/api/_kine-clinical'
import { clinicalToken, totalSessions, validateDecision, message } from '@/utils/kine-reeducation'
import { clinicalDate } from '@/utils/kine-clinical-time'
import { kineSection } from '@/utils/kine-section-fields'
export default {
    name: 'KineReeducation', components: { BModal },
    props: { context: Object, reevaluation: Boolean, consultationId: Number },
    data: () => ({ decision: '', saving: false, seances: 1, statut: 'terminee', motif: '', error: '' }),
    computed: {
        identity() { const user = this.$store.getters.authInfo || {}, role = this.$store.getters.activeRole || {}; return JSON.stringify([user.id, user.access_token, role.id, role.nom]) },
        total() { return totalSessions(this.context.reeducation) },
        prescriptionDate() {
            const fields = (((this.context.bilan || {}).rubriques || {}).kine_prescription || [])
            const field = fields.find(f => f.field_name === 'Date de l’ordonnance')
            // Date de l’ordonnance si elle est saisie, sinon celle du bilan initial.
            if (field && /^\d{4}-\d{2}-\d{2}$/.test(String(field.value))) return String(field.value).split('-').reverse().join('/')
            const bilan = (this.context.bilan || {}).date
            return bilan ? this.date(bilan).slice(0, 10) : 'date non renseignée'
        },
        previous() { return (this.context.seances || []).filter(s => Number(s.numero) < Number(this.context.numero)) }
    },
    methods: {
        recorded(value) { return value === null || value === undefined || value === '' ? 'Non renseignée' : value },
        date(value) { return value ? clinicalDate(value, this.context.fuseau) : 'Date non renseignée' },
        label(slug) { const spec = kineSection(slug); return spec ? spec.title : slug },
        value(value) { return Array.isArray(value) ? value.join(', ') : value === true ? 'Oui' : value === false ? 'Non' : String(value === null || value === undefined ? '' : value).replace(/<[^>]*>/g, ' ').trim() || 'Non renseigné' },
        grid(row, slug) {
            const spec = kineSection(slug), field = spec && spec.fields.find(f => f.kind === 'grid')
            return Object.keys(row).map(key => {
                const column = field && field.columns.find(c => c.key === key)
                return (column ? column.label : key) + ' : ' + this.recorded(row[key])
            }).join(' · ')
        },
        beforeHide(event) { if (this.saving) event.preventDefault(); else this.decision = '' },
        async save() {
            if (this.saving || !this.decision) return
            const extend = this.decision === 'prolonger', data = { seances: this.seances, statut: this.statut, motif: this.motif.trim() }
            this.error = validateDecision(data, extend)
            if (this.error) return
            const id = this.context.reeducation.id, consultation = this.consultationId, identity = this.identity
            const current = () => !this._isDestroyed && identity === this.identity && this.consultationId === consultation && this.context.reeducation && this.context.reeducation.id === id
            this.saving = true
            try {
                const token = await clinicalToken(current)
                const result = await (extend ? api.extendReeducation(id, data, token) : api.finishReeducation(id, data, token))
                if (!current()) return
                this.$set(this.context, 'reeducation', result.data.reeducation || result.data)
                this.decision = ''; this.motif = ''; this.$emit('updated')
            } catch (error) { if (current()) this.error = message(error) }
            finally { if (!this._isDestroyed) this.saving = false }
        }
    }
}
</script>
<style lang="scss">
@import '@/styles/kine-reeducation.scss';
.kine-reeducation summary { cursor: pointer; font-weight: 600; }
.kine-reeducation dl { margin-bottom: 8px; overflow-wrap: anywhere; }
</style>
